import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ThemedTable } from "../../../../../common/tables/ThemedTable";
import { MUIDataTableColumn } from "mui-datatables";
import { LoadingSpinner } from "../../../../../common";
import { companyDialogTableOptions, headerCellProps } from "../../utils/companyDialogTableOptions";
import { EntitlementRow } from "components/companyPage/types";
import CloseIcon from "@mui/icons-material/Close";
import { badgeEntitlementsDialogStyles } from "./styles/BadgeEntitlementsDialog.styles";

type BadgeEntitlementsDialogProps = {
  open: boolean;
  onClose: () => void;
  entitlements: EntitlementRow[];
  isEntitlementsLoading: boolean;
  companyName?: string;
};

export const BadgeEntitlementsDialog: React.FC<BadgeEntitlementsDialogProps> = ({
  open,
  onClose,
  entitlements,
  isEntitlementsLoading,
  companyName
}) => {
  const { t } = useTranslation("common");
  const columns: MUIDataTableColumn[] = [
    {
      name: "badgeProduct",
      label: t("companyPage.badgeProduct"),
      options: {
        sort: false,
        filter: false,
        setCellHeaderProps: headerCellProps,
        customBodyRender: (value: string) => <Box sx={badgeEntitlementsDialogStyles.badgeProductCell}>{value}</Box>
      }
    },
    {
      name: "entitlements",
      label: t("companyPage.entitlementsDialog.entitlements"),
      options: { sort: false, filter: false, setCellHeaderProps: headerCellProps }
    }
  ];

  const hasEntitlements = entitlements?.length > 0;
  const showNoEntitlementsState = !isEntitlementsLoading && !hasEntitlements;

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onClose();
      }}
      maxWidth={showNoEntitlementsState ? "xs" : "sm"}
      fullWidth
    >
      <DialogTitle sx={badgeEntitlementsDialogStyles.dialogTitleContainer}>
        <Typography sx={badgeEntitlementsDialogStyles.dialogTitle}>{t("companyPage.entitlementsDialog.title")}</Typography>
        <CloseIcon color="primary" onClick={onClose} sx={badgeEntitlementsDialogStyles.dialogCloseButton} />
      </DialogTitle>

      <DialogContent sx={badgeEntitlementsDialogStyles.dialogContent}>
        {isEntitlementsLoading && (
          <Box sx={badgeEntitlementsDialogStyles.loading}>
            <LoadingSpinner />
          </Box>
        )}

        {showNoEntitlementsState && (
          <Typography variant="body1" sx={badgeEntitlementsDialogStyles.noEntitlementsMessage}>
            {t("companyPage.entitlementsDialog.noEntitlementsMessage")}
          </Typography>
        )}

        {!isEntitlementsLoading && hasEntitlements && (
          <>
            <Typography variant="body2" sx={badgeEntitlementsDialogStyles.subtitle}>
              {t("companyPage.entitlementsDialog.subtitle", { companyName: companyName || "" })}
            </Typography>

            <ThemedTable cols={columns} tableData={entitlements} options={companyDialogTableOptions} />
          </>
        )}
      </DialogContent>

      <DialogActions sx={badgeEntitlementsDialogStyles.dialogActions}>
        <Button variant="contained" onClick={onClose} sx={badgeEntitlementsDialogStyles.closeButton}>
          {t("common.buttonLabels.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
